import { Network } from "@aptos-labs/ts-sdk";

import { getSdkConfig, network } from "@/contexts/dex-sdk";

const getNetworkParam = () => {
  const sdkConfig = getSdkConfig();
  return sdkConfig.network === Network.CUSTOM ? "decibel" : sdkConfig.network;
};

export const getExplorerUrl = (tx: number | string) => {
  return `https://explorer.aptoslabs.com/txn/${tx}?network=${getNetworkParam()}`;
};

export const getAccountExplorerUrl = (accountAddress: string) => {
  return `https://explorer.aptoslabs.com/account/${accountAddress}?network=${getNetworkParam()}`;
};

/**
 * Explorer page for a fungible asset's metadata object. Distinct from
 * `/coin/<type>::info`, which only exists for legacy `Coin` types — most spot
 * base assets (e.g. NBTC) are native fungible assets with no coin type at all.
 */
export const getFungibleAssetExplorerUrl = (assetAddress: string) => {
  return `https://explorer.aptoslabs.com/fungible_asset/${assetAddress}?network=${getNetworkParam()}`;
};

interface Explorer {
  base: string;
  txPath: string;
  suffix?: string;
}
type ExplorerConfig = Record<string, Explorer>;

const explorerTxUrl = (explorer: Explorer, txHash: string) =>
  `${explorer.base}${explorer.txPath}${txHash}${explorer.suffix ?? ""}`;

const MAINNET_EXPLORERS = {
  Ethereum: { base: "https://etherscan.io", txPath: "/tx/" },
  Arbitrum: { base: "https://arbiscan.io", txPath: "/tx/" },
  Base: { base: "https://basescan.org", txPath: "/tx/" },
  Avalanche: { base: "https://snowtrace.io", txPath: "/tx/" },
  Optimism: { base: "https://optimistic.etherscan.io", txPath: "/tx/" },
  Polygon: { base: "https://polygonscan.com", txPath: "/tx/" },
  Solana: { base: "https://solscan.io", txPath: "/tx/" },
  Sui: { base: "https://suiscan.xyz/mainnet", txPath: "/tx/" },
} satisfies ExplorerConfig;

const TESTNET_EXPLORERS: ExplorerConfig = {
  Sepolia: { base: "https://sepolia.etherscan.io", txPath: "/tx/" },
  ArbitrumSepolia: { base: "https://sepolia.arbiscan.io", txPath: "/tx/" },
  BaseSepolia: { base: "https://sepolia.basescan.org", txPath: "/tx/" },
  // v2 transfer cards store the mainnet chain name even on testnet.
  Ethereum: { base: "https://sepolia.etherscan.io", txPath: "/tx/" },
  Arbitrum: { base: "https://sepolia.arbiscan.io", txPath: "/tx/" },
  Base: { base: "https://sepolia.basescan.org", txPath: "/tx/" },
  Avalanche: { base: "https://testnet.snowtrace.io", txPath: "/tx/" },
  OptimismSepolia: { base: "https://sepolia-optimism.etherscan.io", txPath: "/tx/" },
  PolygonSepolia: { base: "https://amoy.polygonscan.com", txPath: "/tx/" },
  Solana: { base: "https://solscan.io", txPath: "/tx/", suffix: "?cluster=devnet" },
  Sui: { base: "https://suiscan.xyz/testnet", txPath: "/tx/" },
};

/** `chainNetwork` is where the transaction ran, which can differ from the app's for a v1 CCTP transfer. */
export function getSourceChainExplorerUrl(
  sourceChain: string,
  txHash: string,
  chainNetwork: Network = network,
): string | null {
  const explorers: ExplorerConfig =
    chainNetwork === Network.MAINNET ? MAINNET_EXPLORERS : TESTNET_EXPLORERS;
  const explorer = explorers[sourceChain];
  if (!explorer) return null;
  return explorerTxUrl(explorer, txHash);
}

// Keyed by Fun's chain ids, not chain names. Fun is mainnet-only.
const FUN_CHAIN_EXPLORERS: ExplorerConfig = {
  "1": MAINNET_EXPLORERS.Ethereum,
  "10": MAINNET_EXPLORERS.Optimism,
  "56": { base: "https://bscscan.com", txPath: "/tx/" },
  "137": MAINNET_EXPLORERS.Polygon,
  "143": { base: "https://monadscan.com", txPath: "/tx/" },
  "999": { base: "https://hyperevmscan.io", txPath: "/tx/" },
  "4663": { base: "https://robinhoodchain.blockscout.com", txPath: "/tx/" },
  "8453": MAINNET_EXPLORERS.Base,
  "42161": MAINNET_EXPLORERS.Arbitrum,
  "57073": { base: "https://explorer.inkonchain.com", txPath: "/tx/" },
  "1151111081099710": MAINNET_EXPLORERS.Solana,
  "8253038": { base: "https://mempool.space", txPath: "/tx/" },
  "728126428": { base: "https://tronscan.org", txPath: "/transaction/" },
};

/** Explorer link for a transaction on one of Fun's chains; null for any other chain id. */
export function getFunChainTxUrl(chainId: string, txHash: string): string | null {
  const explorer = FUN_CHAIN_EXPLORERS[chainId];
  return explorer ? explorerTxUrl(explorer, txHash) : null;
}

export const getAddressPortfolioPathname = (address: string) => {
  return `/portfolio/${address}`;
};
